/**
 * Indian market hours utilities.
 * NSE/BSE: 9:15 AM - 3:40 PM IST, Monday-Friday, excluding trading holidays.
 *
 * The optional `holidays` map (date "YYYY-MM-DD" → display name, as served by
 * GET /reference-data?type=holidays) makes the status holiday-aware. When it's
 * omitted the helpers fall back to weekend-only logic (back-compat for callers
 * that haven't loaded the list yet).
 */

import { MARKET_HOURS } from '../constants/tradingConstants';

const MARKET_OPEN_HOUR = MARKET_HOURS.OPEN_HOUR;
const MARKET_OPEN_MINUTE = MARKET_HOURS.OPEN_MINUTE;
const MARKET_CLOSE_HOUR = MARKET_HOURS.CLOSE_HOUR;
const MARKET_CLOSE_MINUTE = MARKET_HOURS.CLOSE_MINUTE;

type Holidays = Record<string, string>;

function getIST(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
}

/** YYYY-MM-DD from a Date whose local fields already hold IST wall-clock
 *  values (i.e. the Date returned by getIST() or derived from it). */
function dateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** Holiday display name for a given IST Date, or null if it's a trading day. */
function holidayName(d: Date, holidays?: Holidays): string | null {
  return holidays?.[dateStr(d)] ?? null;
}

export function isMarketOpen(holidays?: Holidays): boolean {
  const now = getIST();
  const day = now.getDay(); // 0=Sun, 6=Sat
  if (day === 0 || day === 6) return false;
  if (holidayName(now, holidays)) return false;

  const minutes = now.getHours() * 60 + now.getMinutes();
  const openAt = MARKET_OPEN_HOUR * 60 + MARKET_OPEN_MINUTE;
  const closeAt = MARKET_CLOSE_HOUR * 60 + MARKET_CLOSE_MINUTE;
  return minutes >= openAt && minutes < closeAt;
}

export function getMarketStatusText(holidays?: Holidays): string {
  const now = getIST();
  const day = now.getDay();
  const minutes = now.getHours() * 60 + now.getMinutes();
  const openAt = MARKET_OPEN_HOUR * 60 + MARKET_OPEN_MINUTE;
  const closeAt = MARKET_CLOSE_HOUR * 60 + MARKET_CLOSE_MINUTE;

  const isWeekday = day >= 1 && day <= 5;
  const todayHoliday = isWeekday ? holidayName(now, holidays) : null;

  // Weekday holiday — surface which holiday it is so the user knows why the
  // market's shut today, then point them at the next session.
  if (todayHoliday) {
    return `Market Holiday — ${todayHoliday}, opens ${nextOpenLabel(now, holidays)}`;
  }

  if (isWeekday && minutes >= openAt && minutes < closeAt) {
    const remaining = closeAt - minutes;
    const h = Math.floor(remaining / 60);
    const m = remaining % 60;
    return `Market Open — closes in ${h > 0 ? `${h}h ` : ''}${m}m`;
  }

  if (isWeekday && minutes < openAt) {
    // Today before open (and not a holiday).
    const until = openAt - minutes;
    const h = Math.floor(until / 60);
    const m = until % 60;
    return `Market Closed — opens in ${h > 0 ? `${h}h ` : ''}${m}m`;
  }

  // After hours / weekend — show next open day (skipping weekends + holidays).
  return `Market Closed — opens ${nextOpenLabel(now, holidays)}`;
}

/** Day label (e.g. "Mon 9:15 AM") of the next trading session after `now`,
 *  skipping weekends and any holidays in the map. */
function nextOpenLabel(now: Date, holidays?: Holidays): string {
  const next = new Date(now);
  next.setHours(MARKET_OPEN_HOUR, MARKET_OPEN_MINUTE, 0, 0);
  // Real holiday runs never exceed a few consecutive non-trading days; the
  // 14-day cap is a defensive bound so the loop provably terminates even if a
  // pathological holidays map were ever supplied.
  let daysUntil = 0;
  do {
    daysUntil++;
    next.setDate(now.getDate() + daysUntil);
  } while (
    (next.getDay() === 0 || next.getDay() === 6 || holidayName(next, holidays))
    && daysUntil < 14
  );

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${dayNames[next.getDay()]} 9:15 AM`;
}

/** Returns today's date in YYYY-MM-DD format in IST timezone */
export function getTodayIST(): string {
  const now = new Date();
  return now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // en-CA gives YYYY-MM-DD
}
